import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loading } from "../../redux/actions/authAction";
import { getDataAPIAuth, postDataAPI, putDataAPI, deleteDataAPI } from "../../utils/API";
import { dialog } from "../Common/TreasuryDialog";
import AdminShell from "./AdminShell";

/**
 * Tokenization — the regulated opportunity pipeline (CMA FinTech Lab model).
 *
 *   received → due diligence → approved → tokenized
 *
 * An opportunity is received from the platform or entered by the admin,
 * passes the Phase-1 due-diligence gates, and is then tokenized: one
 * property, one ERC-3643 token series, deployed on the Treasury STO Engine
 * (permissioned Besu QBFT). Investor admission lives in the Users zone;
 * this page manages assets and their series lifecycle.
 */

/* ── design tokens (match admin suite) ── */
const card = { background: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", overflow: "hidden" };
const TH = { padding: "11px 16px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap", textAlign: "left" };
const TD = { padding: "13px 16px", fontSize: 13.5, color: "#374151", borderBottom: "1px solid #f3f4f6", verticalAlign: "middle" };
const overlay = { direction: "ltr", position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" };
const modalBox = (w = 480) => ({ background: "#fff", borderRadius: 14, width: "100%", maxWidth: w, padding: "28px 32px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", position: "relative", margin: "auto 0" });
const inputS = { width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13.5, color: "#111827", background: "#f9fafb", outline: "none", boxSizing: "border-box", marginTop: 6 };
const LBL = ({ children }) => <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block" }}>{children}</label>;

const Btn = ({ variant = "default", onClick, children, type = "button", full, disabled, small }) => {
  const styles = {
    primary: { background: "#1a2035", color: "#fff" },
    gold: { background: "#b8860b", color: "#fff" },
    success: { background: "#16a34a", color: "#fff" },
    danger: { background: "#fee2e2", color: "#dc2626" },
    warn: { background: "#fef3c7", color: "#92400e" },
    default: { background: "#f3f4f6", color: "#374151" },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ border: "none", borderRadius: 7, padding: small ? "5px 10px" : "8px 14px", fontSize: small ? 11.5 : 12.5, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1, display: "inline-flex", alignItems: "center", gap: 5, width: full ? "100%" : "auto", justifyContent: "center", ...styles[variant] }}>
      {children}
    </button>
  );
};

const STATUS_META = {
  received: { label: "Received", bg: "rgba(107,114,128,0.1)", color: "#6b7280" },
  due_diligence: { label: "Due Diligence", bg: "rgba(245,158,11,0.12)", color: "#92400e" },
  approved: { label: "Approved", bg: "rgba(59,130,246,0.12)", color: "#1d4ed8" },
  tokenizing: { label: "Deploying…", bg: "rgba(184,134,11,0.14)", color: "#92600a" },
  tokenized: { label: "Tokenized", bg: "rgba(22,163,74,0.12)", color: "#15803d" },
  rejected: { label: "Rejected", bg: "rgba(220,38,38,0.1)", color: "#dc2626" },
};
const StatusChip = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.received;
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: m.bg, color: m.color, whiteSpace: "nowrap" }}>{m.label}</span>;
};

const ASSET_TYPES = [
  { value: "real_estate", label: "Real Estate (Phase 1)" },
  { value: "fund_unit", label: "Fund Units (Phase 2)" },
  { value: "equity", label: "Equity / Shares (Phase 3)" },
  { value: "debt_instrument", label: "Debt Instrument / Sukuk (Phase 3)" },
  { value: "rwa", label: "Other Real-World Asset (Phase 3)" },
  { value: "other", label: "Other CMA-Approved Security" },
];
const assetLabel = (v) => (ASSET_TYPES.find((a) => a.value === v)?.label || v || "Real Estate").replace(/ \(Phase \d\)$/, "");

const sar = (v) => `﷼ ${Number(v || 0).toLocaleString()}`;
const short = (a) => (a ? `${a.slice(0, 8)}…${a.slice(-6)}` : "—");

const AdminTokenization = () => {
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;

  const [engineStatus, setEngineStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stoInvestors, setStoInvestors] = useState([]);

  /* create / import modals */
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", assetType: "real_estate", location: "", propertyType: "", valueSAR: "", expectedYieldPct: "", description: "" });
  const [createError, setCreateError] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importPropertyId, setImportPropertyId] = useState("");
  const [importError, setImportError] = useState("");

  /* detail */
  const [detail, setDetail] = useState(null); // opportunity object
  const [onChain, setOnChain] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [noteDraft, setNoteDraft] = useState({});

  /* tokenize form */
  const [tokSymbol, setTokSymbol] = useState("");
  const [tokPrice, setTokPrice] = useState("100");
  const [windowHours, setWindowHours] = useState("24");

  /* allocate */
  const [allocOpen, setAllocOpen] = useState(false);
  const [allocRows, setAllocRows] = useState([{ userId: "", units: "" }]);
  const [allocError, setAllocError] = useState("");
  const [allocSuccess, setAllocSuccess] = useState("");

  /* series data */
  const [holdings, setHoldings] = useState(null);
  const [events, setEvents] = useState([]);

  const loadAll = useCallback(async () => {
    if (!token) return;
    try {
      const [ops, st] = await Promise.all([
        getDataAPIAuth("admin/opportunities", token),
        getDataAPIAuth("admin/sto/status", token),
      ]);
      if (ops.data.status === 1) setRows(ops.data.data);
      setEngineStatus(st.data);
    } catch (e) { /* surfaced via UI states */ }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadAux = useCallback(async () => {
    if (!token) return;
    try {
      const [props, inv] = await Promise.all([
        getDataAPIAuth("get_property?status=1", token),
        getDataAPIAuth("admin/sto/investors", token),
      ]);
      const p = props.data?.data || (Array.isArray(props.data) ? props.data : []);
      setProperties(p);
      if (inv.data.status === 1) setStoInvestors(inv.data.data);
    } catch (e) { /* non-fatal */ }
  }, [token]);
  useEffect(() => { loadAux(); }, [loadAux]);

  // While a series deployment runs in the background (~25 on-chain
  // transactions, ≈2 minutes), poll the pipeline so the page updates itself.
  useEffect(() => {
    const deploying = detail?.status === "tokenizing" || rows.some((r) => r.status === "tokenizing");
    if (!deploying) return undefined;
    const t = setInterval(async () => {
      await loadAll();
      if (detail?._id) {
        const res = await getDataAPIAuth(`admin/opportunities/${detail._id}`, token);
        if (res.data.status === 1) { setDetail(res.data.data); setOnChain(res.data.onChain); }
      }
    }, 5000);
    return () => clearInterval(t);
  }, [detail?.status, detail?._id, rows, loadAll, token]);

  /* ── detail loading ── */
  const openDetail = async (id) => {
    setDetailError(""); setHoldings(null); setEvents([]); setAllocSuccess("");
    try {
      const res = await getDataAPIAuth(`admin/opportunities/${id}`, token);
      if (res.data.status === 1) {
        setDetail(res.data.data);
        setOnChain(res.data.onChain);
        setTokSymbol(res.data.data.series?.symbol || suggestSymbol());
        setTokPrice(String(res.data.data.series?.tokenPriceSAR || 100));
        if (res.data.data.series?.seriesId) loadSeriesData(id);
      }
    } catch (e) { setDetailError(e.response?.data?.msg || "Failed to load"); }
  };

  const refreshDetail = async () => { if (detail?._id) await openDetail(detail._id); await loadAll(); };

  const loadSeriesData = async (id) => {
    try {
      const [h, ev] = await Promise.all([
        getDataAPIAuth(`admin/opportunities/${id}/series/holdings`, token),
        getDataAPIAuth(`admin/opportunities/${id}/series/events`, token),
      ]);
      if (h.data.status === 1) setHoldings(h.data);
      if (ev.data.status === 1) setEvents(ev.data.events || []);
    } catch (e) { /* series panel shows its own error states */ }
  };

  const suggestSymbol = () => {
    const n = rows.filter((r) => r.status === "tokenized").length + 1;
    return `TRE-${String(n).padStart(3, "0")}`;
  };

  /* ── actions ── */
  const call = async (fn, errSetter = setDetailError) => {
    dispatch(loading(true));
    try { await fn(); }
    catch (e) { errSetter(e.response?.data?.msg || e.message || "Something went wrong"); }
    finally { dispatch(loading(false)); }
  };

  const createSubmit = (e) => {
    e.preventDefault(); setCreateError("");
    call(async () => {
      const res = await postDataAPI("admin/opportunities", createForm, token);
      if (res.data.status === 1) { setCreateOpen(false); setCreateForm({ name: "", assetType: "real_estate", location: "", propertyType: "", valueSAR: "", expectedYieldPct: "", description: "" }); await loadAll(); }
    }, setCreateError);
  };

  const importSubmit = (e) => {
    e.preventDefault(); setImportError("");
    call(async () => {
      const res = await postDataAPI(`admin/opportunities/import/${importPropertyId}`, {}, token);
      if (res.data.status === 1) { setImportOpen(false); setImportPropertyId(""); await loadAll(); }
    }, setImportError);
  };

  const setCheck = (key, status) =>
    call(async () => {
      const res = await postDataAPI(`admin/opportunities/${detail._id}/check`, { key, status, note: noteDraft[key] }, token);
      if (res.data.status === 1) { setDetail(res.data.data); await loadAll(); }
    });

  const tokenize = () =>
    call(async () => {
      const ok = await dialog.confirm("Deploy Token Series", `Deploy ${tokSymbol} (${capUnits().toLocaleString()} units × ﷼${Number(tokPrice).toLocaleString()}) on the STO Engine? Deployment runs ~25 on-chain transactions and takes about 2 minutes; the series starts paused with an inactive issue record.`);
      if (!ok) return;
      const res = await postDataAPI(`admin/opportunities/${detail._id}/tokenize`, { symbol: tokSymbol, tokenPriceSAR: Number(tokPrice) }, token);
      if (res.data.status === 1) { setDetail(res.data.data); await loadAll(); }
    });

  const seriesAction = (path, body, confirmMsg) =>
    call(async () => {
      if (confirmMsg) { const ok = await dialog.confirm("Confirm", confirmMsg); if (!ok) return; }
      const res = await postDataAPI(`admin/opportunities/${detail._id}/series/${path}`, body, token);
      if (res.data.status === 1) await refreshDetail();
    });

  const rejectOpportunity = (row) =>
    call(async () => {
      const ok = await dialog.confirm("Reject Opportunity", `Reject "${row.name}"?`);
      if (!ok) return;
      await postDataAPI(`admin/opportunities/${row._id}/reject`, {}, token);
      await loadAll();
    });

  const deleteOpportunity = (row) =>
    call(async () => {
      const ok = await dialog.confirm("Delete Opportunity", "This cannot be undone.");
      if (!ok) return;
      await deleteDataAPI(`admin/opportunities/${row._id}`, token);
      await loadAll();
    });

  const [autoAllocResult, setAutoAllocResult] = useState(null);
  const autoAllocate = () =>
    call(async () => {
      const ok = await dialog.confirm("Auto-Allocate Subscribed", "Sweep this property\u2019s subscriptions: eligible investors already mint automatically when they subscribe \u2014 this tops up anyone missed (e.g. approved into the tokenization base afterwards). Already-allocated investors are skipped.");
      if (!ok) return;
      setAutoAllocResult(null);
      const res = await postDataAPI(`admin/opportunities/${detail._id}/series/allocate-subscribed`, {}, token);
      if (res.data.status === 1) {
        setAutoAllocResult(res.data);
        await loadSeriesData(detail._id);
        await refreshDetail();
      }
    });

  const allocSubmit = (e) => {
    e.preventDefault(); setAllocError(""); setAllocSuccess("");
    const allocations = allocRows.filter((r) => r.userId && r.units);
    if (!allocations.length) { setAllocError("Add at least one allocation"); return; }
    call(async () => {
      const res = await postDataAPI(`admin/opportunities/${detail._id}/series/allocate`, { allocations }, token);
      if (res.data.status === 1) {
        setAllocSuccess(`Minted — tx ${short(res.data.result?.txHash)}`);
        setAllocRows([{ userId: "", units: "" }]);
        await loadSeriesData(detail._id);
        await refreshDetail();
      }
    }, setAllocError);
  };

  const capUnits = () => {
    const p = Number(tokPrice);
    return p > 0 && detail ? Math.floor(detail.valueSAR / p) : 0;
  };

  const ddProgress = (op) => {
    const c = op.dueDiligence || [];
    return { passed: c.filter((x) => x.status === "passed").length, total: c.length };
  };

  /* ── derived ── */
  const stats = {
    total: rows.filter((r) => r.status !== "rejected").length,
    dd: rows.filter((r) => ["received", "due_diligence"].includes(r.status)).length,
    tokenized: rows.filter((r) => r.status === "tokenized").length,
    valueTokenized: rows.filter((r) => r.status === "tokenized").reduce((a, r) => a + (r.valueSAR || 0), 0),
  };
  const engineOk = engineStatus?.configured && engineStatus?.health?.ok;

  return (
    <AdminShell pageTitle="Tokenization">
      {/* ── engine status banner ── */}
      <div style={{ ...card, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", borderLeft: `4px solid ${engineOk ? "#16a34a" : "#f59e0b"}` }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: engineOk ? "#16a34a" : "#f59e0b", flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 240 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: "#111827" }}>STO Engine</span>
          <span style={{ fontSize: 12.5, color: "#6b7280", marginLeft: 10 }}>
            {!engineStatus ? "Checking…"
              : !engineStatus.configured ? "Not configured — set STO_ENGINE_URL / STO_ENGINE_API_KEY"
              : engineStatus.error ? `Unreachable: ${engineStatus.error}`
              : `Connected · ${engineStatus.tenant?.tenant?.name || "tenant"} · Besu QBFT block #${engineStatus.health?.chain?.blockNumber ?? "—"} · ${engineStatus.tenant?.series ?? 0} series · ${engineStatus.tenant?.investors ?? 0} investors on-chain`}
          </span>
        </div>
        <a href="/admin/integrations/sto-engine" style={{ fontSize: 12, fontWeight: 600, color: "#b8860b", textDecoration: "none" }}>Integration settings →</a>
      </div>

      {/* ── stats ── */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          { label: "Pipeline", value: stats.total, color: "#1a2035" },
          { label: "In Due Diligence", value: stats.dd, color: "#b45309" },
          { label: "Tokenized", value: stats.tokenized, color: "#15803d" },
          { label: "Value Tokenized", value: sar(stats.valueTokenized), color: "#b8860b" },
        ].map((s) => (
          <div key={s.label} style={{ ...card, padding: "18px 24px", minWidth: 160, flex: 1 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</p>
            <p style={{ margin: "6px 0 0", fontSize: 26, fontWeight: 800, color: s.color, whiteSpace: "nowrap" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* ── pipeline table ── */}
      <div style={card}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Opportunity Pipeline</h2>
            <p style={{ margin: "3px 0 0", fontSize: 12, color: "#9ca3af" }}>received → due diligence → approved → tokenized · one property, one token series</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="default" onClick={() => { setImportError(""); setImportOpen(true); }}>Import from Properties</Btn>
            <Btn variant="gold" onClick={() => { setCreateError(""); setCreateOpen(true); }}>+ New Opportunity</Btn>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>{["Opportunity", "Source", "Value", "Due Diligence", "Status", "Series", "Actions"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ ...TD, textAlign: "center", padding: 40, color: "#9ca3af" }}>No opportunities yet — add one or import a platform property.</td></tr>
              ) : rows.map((r) => {
                const p = ddProgress(r);
                return (
                  <tr key={r._id} style={{ transition: "background 0.15s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={TD}>
                      <div style={{ fontWeight: 600, color: "#111827" }}>{r.name}</div>
                      <div style={{ fontSize: 11.5, color: "#9ca3af" }}>{assetLabel(r.assetType)}{r.location ? ` · ${r.location}` : ""}{r.propertyType ? ` · ${r.propertyType}` : ""}</div>
                    </td>
                    <td style={TD}>{r.source === "platform" ? "Platform listing" : "Admin"}</td>
                    <td style={{ ...TD, whiteSpace: "nowrap" }}>{sar(r.valueSAR)}</td>
                    <td style={TD}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 70, height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
                          <div style={{ width: `${(p.passed / p.total) * 100}%`, height: "100%", background: p.passed === p.total ? "#16a34a" : "#b8860b" }} />
                        </div>
                        <span style={{ fontSize: 11.5, color: "#6b7280" }}>{p.passed}/{p.total}</span>
                      </div>
                    </td>
                    <td style={TD}><StatusChip status={r.status} /></td>
                    <td style={TD}>{r.series?.symbol ? <span style={{ fontFamily: "monospace", fontSize: 12.5, fontWeight: 700, color: "#b8860b" }}>{r.series.symbol}</span> : "—"}</td>
                    <td style={TD}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Btn variant="primary" small onClick={() => openDetail(r._id)}>Open</Btn>
                        {r.status !== "tokenized" && r.status !== "rejected" && <Btn variant="warn" small onClick={() => rejectOpportunity(r)}>Reject</Btn>}
                        {r.status !== "tokenized" && <Btn variant="danger" small onClick={() => deleteOpportunity(r)}>Delete</Btn>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── create modal ── */}
      {createOpen && (
        <div style={overlay} onClick={() => setCreateOpen(false)}>
          <div style={modalBox(520)} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setCreateOpen(false)} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22 }}>×</button>
            <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#111827" }}>New Opportunity</h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#9ca3af" }}>Enter an asset received outside the platform — a property, fund units, equity, a debt instrument, or another CMA-approved security. It starts in Due Diligence.</p>
            <form onSubmit={createSubmit}>
              {createError && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 14 }}>{createError}</div>}
              <div style={{ marginBottom: 12 }}>
                <LBL>Asset class *</LBL>
                <select style={inputS} value={createForm.assetType} onChange={(e) => setCreateForm({ ...createForm, assetType: e.target.value })}>
                  {ASSET_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                <p style={{ margin: "6px 0 0", fontSize: 11, color: "#9ca3af" }}>Each class carries its own due-diligence gates. Phases 2–3 require prior CMA approval of the product design.</p>
              </div>
              <div style={{ marginBottom: 12 }}><LBL>Asset name *</LBL><input style={inputS} value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required placeholder="e.g. Al Malqa Residential Tower" /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div><LBL>Location</LBL><input style={inputS} value={createForm.location} onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })} placeholder="Riyadh — Al Malqa" /></div>
                <div><LBL>Type</LBL><input style={inputS} value={createForm.propertyType} onChange={(e) => setCreateForm({ ...createForm, propertyType: e.target.value })} placeholder="Residential" /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div><LBL>Value (SAR) *</LBL><input style={inputS} type="number" min="1" value={createForm.valueSAR} onChange={(e) => setCreateForm({ ...createForm, valueSAR: e.target.value })} required placeholder="10000000" /></div>
                <div><LBL>Expected yield %</LBL><input style={inputS} type="number" step="0.1" value={createForm.expectedYieldPct} onChange={(e) => setCreateForm({ ...createForm, expectedYieldPct: e.target.value })} placeholder="10.0" /></div>
              </div>
              <div style={{ marginBottom: 20 }}><LBL>Description</LBL><textarea style={{ ...inputS, minHeight: 70 }} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} /></div>
              <Btn type="submit" variant="gold" full>Add to Pipeline</Btn>
            </form>
          </div>
        </div>
      )}

      {/* ── import modal ── */}
      {importOpen && (
        <div style={overlay} onClick={() => setImportOpen(false)}>
          <div style={modalBox(460)} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setImportOpen(false)} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22 }}>×</button>
            <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#111827" }}>Import Platform Property</h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#9ca3af" }}>Promote a live property listing into the tokenization pipeline.</p>
            <form onSubmit={importSubmit}>
              {importError && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 14 }}>{importError}</div>}
              <div style={{ marginBottom: 20 }}>
                <LBL>Property</LBL>
                <select style={inputS} value={importPropertyId} onChange={(e) => setImportPropertyId(e.target.value)} required>
                  <option value="">— Choose a property —</option>
                  {properties.map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
                </select>
              </div>
              <Btn type="submit" variant="gold" full>Import</Btn>
            </form>
          </div>
        </div>
      )}

      {/* ── detail modal ── */}
      {detail && (
        <div style={overlay} onClick={() => setDetail(null)}>
          <div style={modalBox(860)} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setDetail(null)} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22 }}>×</button>

            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
              <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: "#111827" }}>{detail.name}</h3>
              <StatusChip status={detail.status} />
              {detail.series?.symbol && <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: "#b8860b" }}>{detail.series.symbol}</span>}
            </div>
            <p style={{ margin: "0 0 18px", fontSize: 12.5, color: "#9ca3af" }}>
              {assetLabel(detail.assetType)} · {detail.location || "—"} · {sar(detail.valueSAR)}{detail.expectedYieldPct ? ` · ${detail.expectedYieldPct}% expected yield` : ""} · {detail.source === "platform" ? "platform listing" : "admin-entered"}
            </p>
            {detailError && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 14 }}>{detailError}</div>}

            {/* due diligence */}
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 18, overflow: "hidden" }}>
              <div style={{ padding: "12px 18px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: 13, fontWeight: 700, color: "#111827" }}>
                Due-Diligence Gates <span style={{ fontWeight: 500, color: "#9ca3af" }}>· all must pass before tokenization{detail.status === "tokenized" ? " · locked (tokenized)" : ""}</span>
              </div>
              {detail.dueDiligence.map((c) => (
                <div key={c.key} style={{ padding: "11px 18px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ width: 20, textAlign: "center", fontSize: 14 }}>
                    {c.status === "passed" ? "✅" : c.status === "failed" ? "❌" : "⬜"}
                  </span>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{c.label}</div>
                    {c.note && <div style={{ fontSize: 11.5, color: "#9ca3af" }}>{c.note}</div>}
                  </div>
                  {detail.status !== "tokenized" && detail.status !== "rejected" && (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input style={{ ...inputS, marginTop: 0, width: 180, padding: "6px 10px", fontSize: 12 }} placeholder="Note / reference…" value={noteDraft[c.key] ?? ""} onChange={(e) => setNoteDraft({ ...noteDraft, [c.key]: e.target.value })} />
                      <Btn variant="success" small onClick={() => setCheck(c.key, "passed")}>Pass</Btn>
                      <Btn variant="danger" small onClick={() => setCheck(c.key, "failed")}>Fail</Btn>
                      {c.status !== "pending" && <Btn variant="default" small onClick={() => setCheck(c.key, "pending")}>Reset</Btn>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* tokenize */}
            {detail.status === "approved" && (
              <div style={{ border: "1px solid #b8860b33", background: "#fffbeb", borderRadius: 10, padding: "16px 18px", marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 10 }}>Tokenize — deploy the ERC-3643 series</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div><LBL>Series symbol</LBL><input style={{ ...inputS, width: 140 }} value={tokSymbol} onChange={(e) => setTokSymbol(e.target.value.toUpperCase())} /></div>
                  <div><LBL>Token price (SAR)</LBL><input style={{ ...inputS, width: 130 }} type="number" min="1" value={tokPrice} onChange={(e) => setTokPrice(e.target.value)} /></div>
                  <div style={{ fontSize: 12.5, color: "#92400e", paddingBottom: 10 }}>= <b>{capUnits().toLocaleString()}</b> units ({sar(detail.valueSAR)} ÷ ﷼{Number(tokPrice || 0).toLocaleString()})</div>
                  <Btn variant="gold" onClick={tokenize} disabled={!engineOk}>Deploy on STO Engine</Btn>
                </div>
                {!engineOk && <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "#dc2626" }}>Engine is not reachable — check the integration.</p>}
              </div>
            )}

            {/* deployment in progress */}
            {detail.status === "tokenizing" && (
              <div style={{ border: "1px solid #b8860b33", background: "#fffbeb", borderRadius: 10, padding: "16px 18px", marginBottom: 18, display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 22, height: 22, border: "3px solid #f3e8c8", borderTopColor: "#b8860b", borderRadius: "50%", animation: "trs-spin 0.8s linear infinite" }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>Deploying {tokSymbol} on the STO Engine…</div>
                  <div style={{ fontSize: 12, color: "#92600a" }}>≈25 on-chain transactions on the Besu QBFT chain — about 2 minutes. This page updates automatically.</div>
                </div>
              </div>
            )}
            {detail.tokenizationError && detail.status === "approved" && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 12.5, color: "#dc2626", marginBottom: 14 }}>
                Previous deployment failed: {detail.tokenizationError}. Fix the cause and deploy again.
              </div>
            )}

            {/* series panel */}
            {detail.series?.seriesId && (
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", marginBottom: 6 }}>
                <div style={{ padding: "12px 18px", background: "#0e3725", color: "#fff", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>Token Series {detail.series.symbol}</span>
                  <span style={{ fontSize: 11.5, opacity: 0.85 }}>chain {detail.series.chainId} · record #{detail.series.recordId} · {Number(detail.series.capUnits).toLocaleString()} units × ﷼{Number(detail.series.tokenPriceSAR).toLocaleString()}</span>
                  <span style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {onChain && !onChain.error && (
                      <>
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 12, background: onChain.recordActive ? "#16a34a" : "#6b7280" }}>{onChain.recordActive ? "RECORD ACTIVE" : "RECORD INACTIVE"}</span>
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 12, background: onChain.windowOpen ? "#16a34a" : "#6b7280" }}>{onChain.windowOpen ? "WINDOW OPEN" : "WINDOW CLOSED"}</span>
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 12, background: onChain.paused ? "#dc2626" : "#16a34a" }}>{onChain.paused ? "PAUSED" : "LIVE"}</span>
                      </>
                    )}
                  </span>
                </div>

                <div style={{ padding: "14px 18px", display: "flex", gap: 8, flexWrap: "wrap", borderBottom: "1px solid #f3f4f6" }}>
                  {onChain && !onChain.error && !onChain.recordActive && (
                    <Btn variant="success" onClick={() => seriesAction("activate", { active: true }, "Activate the issue record and unpause the token? Do this only after CMA conditions and registry milestones are met.")}>Activate Issue Record</Btn>
                  )}
                  {onChain && !onChain.error && onChain.recordActive && (
                    <>
                      <Btn variant="gold" onClick={() => { setAllocOpen(true); setAllocError(""); setAllocSuccess(""); }}>Allocate Units</Btn>
                      {detail.propertyId && (
                        <Btn variant="success" onClick={autoAllocate} title="Backstop sweep: units mint automatically the moment an eligible investor subscribes. This tops up anyone who was missed (e.g. approved into the tokenization base after subscribing), and also runs automatically when the subscription window closes.">
                          Auto-Allocate Subscribed
                        </Btn>
                      )}
                      {!onChain.windowOpen ? (
                        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                          <select style={{ ...inputS, marginTop: 0, width: 110, padding: "7px 10px", fontSize: 12 }} value={windowHours} onChange={(e) => setWindowHours(e.target.value)}>
                            <option value="24">24 hours</option>
                            <option value="48">48 hours</option>
                            <option value="72">72 hours</option>
                            <option value="168">1 week</option>
                          </select>
                          <Btn variant="primary" onClick={() => seriesAction("window", { open: true, durationHours: Number(windowHours) })}>Open Trading Window</Btn>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                          <Btn variant="warn" onClick={() => seriesAction("window", { open: false })}>Close Trading Window</Btn>
                          {detail.series?.windowClosesAt && (
                            <span style={{ fontSize: 11.5, color: "#92400e", fontWeight: 600 }}>
                              auto-closes {new Date(detail.series.windowClosesAt).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                        </span>
                      )}
                      <Btn variant={onChain.paused ? "success" : "danger"} onClick={() => seriesAction("pause", { paused: !onChain.paused }, onChain.paused ? "Unpause the token?" : "EMERGENCY PAUSE: freeze all transfers of this series?")}>
                        {onChain.paused ? "Unpause" : "Emergency Pause"}
                      </Btn>
                    </>
                  )}
                  <Btn variant="default" onClick={() => refreshDetail()}>Refresh</Btn>
                </div>

                {/* on-chain summary + contracts */}
                <div style={{ padding: "12px 18px", borderBottom: "1px solid #f3f4f6", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                  <div><div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>Minted / Cap</div><div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{onChain && !onChain.error ? `${Number(onChain.minted).toLocaleString()} / ${Number(onChain.capUnits).toLocaleString()}` : "—"}</div></div>
                  <div><div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>Total Supply</div><div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{onChain && !onChain.error ? Number(onChain.totalSupply).toLocaleString() : "—"}</div></div>
                  <div><div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>Token Contract</div><div style={{ fontSize: 12, fontFamily: "monospace", color: "#374151" }} title={detail.series.contracts?.token}>{short(detail.series.contracts?.token)}</div></div>
                  <div><div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>DvP Escrow</div><div style={{ fontSize: 12, fontFamily: "monospace", color: "#374151" }} title={detail.series.contracts?.dvpSettlement}>{short(detail.series.contracts?.dvpSettlement)}</div></div>
                </div>

                {/* auto-allocation result */}
                {autoAllocResult && (
                  <div style={{ padding: "12px 18px", borderBottom: "1px solid #f3f4f6", background: "#f0fdf4" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", marginBottom: 6 }}>
                      Auto-allocation: {autoAllocResult.minted.length} investor(s) minted{autoAllocResult.txHash ? ` — tx ${short(autoAllocResult.txHash)}` : ""}, {autoAllocResult.skipped.length} skipped
                    </div>
                    {autoAllocResult.minted.map((m) => (
                      <div key={m.userId} style={{ fontSize: 12, color: "#166534" }}>✓ {m.name} — {m.units.toLocaleString()} units</div>
                    ))}
                    {autoAllocResult.skipped.map((s) => (
                      <div key={s.userId} style={{ fontSize: 12, color: "#b45309" }}>⚠ {s.name} — {s.reason}</div>
                    ))}
                  </div>
                )}

                {/* holdings */}
                <div style={{ padding: "12px 18px", borderBottom: "1px solid #f3f4f6" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
                    Holdings (on-chain reconciliation)
                    {holdings && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: holdings.consistent ? "#15803d" : "#dc2626" }}>{holdings.consistent ? "✓ consistent" : "⚠ MISMATCH — reportable exception"}</span>}
                  </div>
                  {!holdings ? <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>No holdings data yet.</p> : holdings.holdings.length === 0 ? <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>No investors whitelisted into this series yet.</p> : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>{["Investor", "Wallet", "Units", "Value"].map((h) => <th key={h} style={{ ...TH, padding: "7px 10px" }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {holdings.holdings.map((h) => (
                          <tr key={h.wallet}>
                            <td style={{ ...TD, padding: "8px 10px" }}>{h.user?.name || h.investorRef}</td>
                            <td style={{ ...TD, padding: "8px 10px", fontFamily: "monospace", fontSize: 11.5 }} title={h.wallet}>{short(h.wallet)}</td>
                            <td style={{ ...TD, padding: "8px 10px" }}>{Number(h.balance).toLocaleString()}</td>
                            <td style={{ ...TD, padding: "8px 10px" }}>{sar(Number(h.balance) * (detail.series.tokenPriceSAR || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* CMA event feed */}
                <div style={{ padding: "12px 18px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginBottom: 8 }}>CMA Supervisory Event Feed</div>
                  {events.length === 0 ? <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>No on-chain events yet.</p> : (
                    <div style={{ maxHeight: 200, overflowY: "auto", background: "#0b1220", borderRadius: 8, padding: "10px 14px" }}>
                      {[...events].reverse().map((ev, i) => (
                        <div key={i} style={{ fontFamily: "monospace", fontSize: 11, color: "#93c5fd", padding: "3px 0", borderBottom: "1px solid #1e293b" }}>
                          <span style={{ color: "#fbbf24" }}>#{ev.blockNumber}</span>{" "}
                          <span style={{ color: "#34d399", fontWeight: 700 }}>{ev.type}</span>{" "}
                          <span style={{ color: "#94a3b8" }}>{Object.entries(ev.args || {}).map(([k, v]) => `${k}=${String(v).length > 14 ? short(String(v)) : v}`).join(" ")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── allocate modal ── */}
      {allocOpen && detail && (
        <div style={{ ...overlay, zIndex: 1100 }} onClick={() => setAllocOpen(false)}>
          <div style={modalBox(560)} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setAllocOpen(false)} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 22 }}>×</button>
            <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#111827" }}>Allocate Units — {detail.series?.symbol}</h3>
            <p style={{ margin: "0 0 18px", fontSize: 12.5, color: "#9ca3af" }}>
              Mint allocations to approved investors. Only members of the tokenization base (approved in the Users zone) can receive units. On-chain caps at ﷼{Number(detail.series?.tokenPriceSAR || 100).toLocaleString()}/unit: retail {Math.floor(100000 / (detail.series?.tokenPriceSAR || 100)).toLocaleString()}, qualified {Math.floor(1000000 / (detail.series?.tokenPriceSAR || 100)).toLocaleString()}, institutional {Math.floor(5000000 / (detail.series?.tokenPriceSAR || 100)).toLocaleString()} units — plus the 10% concentration limit.
            </p>
            <form onSubmit={allocSubmit}>
              {allocError && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 12 }}>{allocError}</div>}
              {allocSuccess && <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#15803d", marginBottom: 12 }}>✓ {allocSuccess}</div>}
              {allocRows.map((row, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
                  <select style={{ ...inputS, marginTop: 0, flex: 1 }} value={row.userId} onChange={(e) => { const next = [...allocRows]; next[i] = { ...row, userId: e.target.value }; setAllocRows(next); }}>
                    <option value="">— Investor —</option>
                    {stoInvestors.map((u) => <option key={u._id} value={u._id}>{u.name} ({u.sto?.category})</option>)}
                  </select>
                  <input style={{ ...inputS, marginTop: 0, width: 110 }} type="number" min="1" placeholder="Units" value={row.units} onChange={(e) => { const next = [...allocRows]; next[i] = { ...row, units: e.target.value }; setAllocRows(next); }} />
                  {allocRows.length > 1 && <Btn variant="danger" small onClick={() => setAllocRows(allocRows.filter((_, j) => j !== i))}>×</Btn>}
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <Btn variant="default" onClick={() => setAllocRows([...allocRows, { userId: "", units: "" }])}>+ Row</Btn>
                <div style={{ flex: 1 }} />
                <Btn type="submit" variant="gold">Mint Allocation</Btn>
              </div>
              {stoInvestors.length === 0 && <p style={{ margin: "10px 0 0", fontSize: 11.5, color: "#dc2626" }}>The tokenization base is empty — approve investors in the Users zone first.</p>}
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminTokenization;
